export interface Node {
  id: string;
  type: string;
  data: any;
  parentId?: string;
  html?: string;
}

export interface Edge {
  source: string;
  target: string;
  sourceHandle?: string;
}

/**
 * Check if a node is visible based on its upstream dependencies
 */
export function isNodeVisible(
  nodeId: string,
  nodes: Node[],
  edges: Edge[],
  formValues: Record<string, any>
): boolean {
  // Find incoming edges
  const incomingEdges = edges.filter((edge) => edge.target === nodeId);

  // If no incoming edges, it's a root node.
  if (incomingEdges.length === 0) {
    // Hidden nodes are value-injectors that only make sense when reached
    // through a valid path. An orphaned hidden node (no incoming edges) must
    // NOT be treated as an always-visible root, otherwise fields wired only to
    // hidden nodes would be displayed regardless of
    // the selected path.
    const node = nodes.find((n) => n.id === nodeId);
    if (node && node.data?.type === "hidden") {
      return false;
    }
    return true;
  }

  // Check if any incoming path is valid
  // A node is visible if AT LEAST ONE incoming path is valid (OR logic for multiple incoming edges? Or AND?)
  // Usually in flow charts, multiple incoming edges mean merge.
  // If it's a merge, usually any path leading to it makes it reachable.

  // However, we need to check if the source node is visible AND if the edge condition is met.

  for (const edge of incomingEdges) {
    const sourceNode = nodes.find((n) => n.id === edge.source);
    if (!sourceNode) {
      continue;
    }

    // Recursively check if source node is visible
    if (!isNodeVisible(sourceNode.id, nodes, edges, formValues)) {
      continue;
    }

    // Check edge condition
    // If source node is a splitter (has sourceHandle), check if value matches handle
    if (edge.sourceHandle) {
      // sourceHandle usually corresponds to the option value
      // But we need to know if the source node is a splitter.
      // We can assume if sourceHandle is present, it's a conditional path.

      const sourceValue = formValues[sourceNode.id];

      // If the handle matches the value, then this path is active.
      if (String(sourceValue) === String(edge.sourceHandle)) {
        return true;
      }
    } else {
      // Unconditional edge from a visible node
      // Check if source node is satisfied (filled if required)
      if (sourceNode.data.required) {
        const sourceValue = formValues[sourceNode.id];
        if (!sourceValue || String(sourceValue).trim() === "") {
          continue; // Source not satisfied, this path is not active yet
        }
      }
      return true;
    }
  }

  return false;
}

/**
 * Get all visible nodes in a group
 */
export function getVisibleNodesInGroup(
  groupId: string,
  nodes: Node[],
  edges: Edge[],
  formValues: Record<string, any>
): string[] {
  const groupNodes = nodes.filter((n) => n.parentId === groupId);
  const visibleNodeIds: string[] = [];

  for (const node of groupNodes) {
    if (isNodeVisible(node.id, nodes, edges, formValues)) {
      visibleNodeIds.push(node.id);
    }
  }

  return visibleNodeIds;
}
